import React from 'react';
import { BriefcaseIcon, CarIcon, CheckIcon, ChefHatIcon, CircleIcon, GlobeIcon, GraduationCapIcon, HeartPulseIcon, HomeIcon, MailIcon, MegaphoneIcon, MoreHorizontalIcon, PhoneIcon, ShoppingCartIcon, SparklesIcon, TargetIcon, UploadCloudIcon, UtensilsIcon, VideoIcon } from 'lucide-react';
import { FormQuestion } from '../../types';
import { normalizeOptions } from '../../utils/formOptions';
import { FormField, FormSelect, FormTextarea } from './FormField';
import { Checkbox } from '../ui/Checkbox';
interface QuestionFieldProps {
  question: FormQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  error?: string;
  autoFocus?: boolean;
}

/** Icono sugerido según el texto de la opción. Puramente visual. */
const ICON_RULES: [RegExp, React.ComponentType<{
  className?: string;
}>][] = [[/inmobili/i, HomeIcon], [/cocina|reforma/i, ChefHatIcon], [/automo|coche|motor/i, CarIcon], [/restaura|hosteler/i, UtensilsIcon], [/ecommerce|tienda/i, ShoppingCartIcon], [/servicio/i, BriefcaseIcon], [/salud|bienestar|clínic/i, HeartPulseIcon], [/formaci|academ/i, GraduationCapIcon], [/public|campaña|anuncio/i, MegaphoneIcon], [/v[ií]deo|contenido/i, VideoIcon], [/automatiz|inteligencia|ia\b/i, SparklesIcon], [/cliente|venta|objetivo/i, TargetIcon], [/otro/i, MoreHorizontalIcon]];
function iconFor(option: string) {
  const match = ICON_RULES.find(([re]) => re.test(option));
  return match ? match[1] : CircleIcon;
}
export function QuestionField({
  question,
  value,
  onChange,
  error,
  autoFocus
}: QuestionFieldProps) {
  /* Guarda de renderizado: ninguna opción sin texto llega a la pantalla. */
  const {
    options: safeOptions,
    descriptions: safeDescriptions
  } = normalizeOptions(question.options, question.optionDescriptions);
  function renderOptions(multiple: boolean) {
    const selected = Array.isArray(value) ? value : value ? [String(value)] : [];
    const descriptions = safeDescriptions;
    // Con descripciones o textos largos, una sola columna se lee mejor.
    const single = descriptions.some(d => d.trim() !== '') || safeOptions.some(option => option.length > 42);
    return <div role={multiple ? 'group' : 'radiogroup'} aria-labelledby={`${question.id}-label`} className={`grid gap-3 ${single ? '' : 'sm:grid-cols-2'}`} data-id="element-866" data-mp="17hsj8a9main8">
        {safeOptions.map((option, optionIndex) => {
        const active = selected.includes(option);
        const Icon = iconFor(option);
        return <button key={option} type="button" role={multiple ? 'checkbox' : 'radio'} aria-checked={active} onClick={() => {
          if (multiple) {
            onChange(active ? selected.filter(s => s !== option) : [...selected, option]);
          } else {
            onChange(option);
          }
        }} className={`group flex min-h-[60px] w-full items-start gap-3 rounded-xl border px-4 py-4 text-left text-[15px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${active ? 'border-primary bg-primary/5 shadow-[0_6px_20px_-12px_rgba(61,139,255,0.9)]' : 'border-border bg-background hover:border-primary/40 hover:bg-surface'}`} data-id="element-867" data-mp="sogvetuerxua" data-mpi={optionIndex}>
              <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors ${active ? 'bg-primary text-primary-foreground' : 'bg-surface text-muted-foreground group-hover:text-primary'}`} aria-hidden="true" data-id="element-868" data-mp="2lhdrk6srs38x">
                {active ? <CheckIcon className="h-4 w-4" data-id="element-869" data-mp="2zetzjmqps52s" /> : <Icon className="h-4 w-4" data-id="element-870" data-mp="2zfv9tozwonjg" />}
              </span>
              <span className="min-w-0 flex-1" data-id="element-871" data-mp="34eevrpei1zbt">
                <span className="block font-medium leading-snug text-foreground" data-id="element-872" data-mp="34c2frhsaxz1e">
                  {option}
                </span>
                {descriptions[optionIndex] && <span className="mt-1 block text-sm leading-relaxed text-muted-foreground" data-id="element-873" data-mp="2vc5nz7am8k24">
                    {descriptions[optionIndex]}
                  </span>}
              </span>
            </button>;
      })}
      </div>;
  }
  let control: React.ReactNode;
  switch (question.type) {
    case 'texto_largo':
      control = <FormTextarea id={question.id} rows={5} autoFocus={autoFocus} placeholder={question.placeholder} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} data-id="element-874" data-mp="1z99phqrbfln9" />;
      break;
    case 'seleccion_unica':
      control = renderOptions(false);
      break;
    case 'seleccion_multiple':
      control = renderOptions(true);
      break;
    case 'si_no':
      control = <div role="radiogroup" className="grid gap-3 sm:grid-cols-2" data-id="element-875" data-mp="2i61fsk59z10t">
          {['Sí', 'No'].map(option => {
          const active = value === option;
          return <button key={option} type="button" role="radio" aria-checked={active} onClick={() => onChange(option)} className={`flex min-h-[60px] items-center justify-center rounded-xl border text-[15px] font-medium transition-all ${active ? 'border-primary bg-primary/5 text-foreground' : 'border-border bg-background text-foreground hover:border-primary/40'}`} data-id="element-876" data-mp="295d1rpfvt0nx">
                {option}
              </button>;
        })}
        </div>;
      break;
    case 'desplegable':
      control = <FormSelect id={question.id} placeholder="Selecciona una opción" options={safeOptions.map(o => ({
        value: o,
        label: o
      }))} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} data-id="element-877" data-mp="39e2y40t8no1t" />;
      break;
    case 'casilla':
      control = <Checkbox id={question.id} label={question.placeholder || 'Acepto'} checked={value === 'Sí'} onChange={e => onChange(e.target.checked ? 'Sí' : '')} data-id="element-878" data-mp="3n5pwc66qr6n8" />;
      break;
    case 'archivo':
    case 'imagen':
      control = <label htmlFor={question.id} className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-surface px-6 py-10 text-center transition-colors hover:border-primary/50" data-id="element-879" data-mp="xzqtw61g1e0">
          <UploadCloudIcon className="h-6 w-6 text-muted-foreground" aria-hidden="true" data-id="element-880" data-mp="22r7d5pk2jj9e" />
          <span className="text-sm font-medium text-foreground" data-id="element-881" data-mp="26s95rnjhcx2e">
            {typeof value === 'string' && value ? value : question.type === 'imagen' ? 'Sube una imagen' : 'Adjunta un archivo'}
          </span>
          <span className="text-xs text-muted-foreground" data-id="element-882" data-mp="354v7ld2wuqgg">
            {question.placeholder || 'Haz clic para seleccionar'}
          </span>
          <input id={question.id} type="file" accept={question.type === 'imagen' ? 'image/*' : undefined} className="sr-only" onChange={e => onChange(e.target.files?.[0]?.name ?? '')} data-id="element-883" data-mp="3f1hu06avxpkl" />
        </label>;
      break;
    default:
      {
        const inputType = question.type === 'email' ? 'email' : question.type === 'telefono' ? 'tel' : question.type === 'numero' ? 'number' : question.type === 'url' ? 'url' : question.type === 'fecha' ? 'date' : question.type === 'hora' ? 'time' : 'text';
        const inputIcon = question.type === 'email' ? <MailIcon data-id="element-884" data-mp="fnedhloi4lcx" /> : question.type === 'telefono' ? <PhoneIcon data-id="element-885" data-mp="fk09ya43q63w" /> : question.type === 'url' ? <GlobeIcon data-id="element-886" data-mp="ao1hobfnk3y7" /> : null;
        control = <FormField id={question.id} type={inputType} autoFocus={autoFocus} icon={inputIcon} placeholder={question.placeholder} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value)} data-id="element-887" data-mp="ap32v8qdu6tn" />;
      }
  }
  return <div className="space-y-5 sm:space-y-6" data-id="element-888" data-mp="l8dkeed4xtjy">
      <div className="space-y-1.5 sm:space-y-2" data-id="element-889" data-mp="l7ln8z3w3nrk">
        {/* En móvil el enunciado es algo menor y más compacto, sin perder
            jerarquía respecto a las respuestas. */}
        <h3 id={`${question.id}-label`} className="text-[21px] font-semibold leading-[1.22] tracking-tight text-navy sm:text-[26px] sm:leading-snug" data-id="element-890" data-mp="biahnmg7p18f">
          {question.label}
          {question.required && <span className="ml-1 text-primary" aria-hidden="true" data-id="element-891" data-mp="1l5txi6pwxpi">
              *
            </span>}
        </h3>
        {question.description && <p className="text-[13px] leading-[1.5] text-muted-foreground sm:text-sm sm:leading-relaxed" data-id="element-892" data-mp="6jqw3n5j5rgl">
            {question.description}
          </p>}
      </div>
      {control}
      {error && <p id={`${question.id}-error`} role="alert" className="text-sm font-medium text-red-600" data-id="element-893" data-mp="3tnnjddlqaini">
          {error}
        </p>}
    </div>;
}